import React, { useState } from 'react'
import AppLayout from '@/layouts/AppLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { toast } from 'sonner'
import { Check, Sparkles, CreditCard, Crown, AlertTriangle } from 'lucide-react'
import { format } from 'date-fns'
import { ja } from 'date-fns/locale'

type Subscription = {
  status: string
  currentPeriodEnd: string | null
  cancelAtPeriodEnd: boolean
} | null

type Props = {
  plan: string
  isPro: boolean
  isUnlimited: boolean
  subscription: Subscription
  stripePublicKey: string
  privateRepositoryCount: number
  privateRepositories: string[]
  storageUsedFormatted: string
  storageUsedBytes: number
}

const FREE_STORAGE_LIMIT = 1 * 1024 * 1024 * 1024 // 1GB in bytes
const FREE_PRIVATE_REPO_LIMIT = 1

export default function Billing({
  plan,
  isPro,
  isUnlimited,
  subscription,
  privateRepositoryCount,
  privateRepositories,
  storageUsedFormatted,
  storageUsedBytes,
}: Props) {
  const [loading, setLoading] = useState(false)

  // ダウングレード時に制限を超過するかどうか
  const willExceedPrivateRepoLimit = privateRepositoryCount > FREE_PRIVATE_REPO_LIMIT
  const willExceedStorageLimit = storageUsedBytes > FREE_STORAGE_LIMIT
  const willExceedLimits = willExceedPrivateRepoLimit || willExceedStorageLimit

  const handleUpgrade = async () => {
    setLoading(true)
    try {
      const response = await fetch('/settings/billing/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
      })
      const data = await response.json()
      if (!response.ok) {
        toast.error(data.error || 'エラーが発生しました')
        setLoading(false)
        return
      }
      window.location.href = data.url
    } catch (error) {
      toast.error('エラーが発生しました')
      setLoading(false)
    }
  }

  const handleManage = () => {
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/settings/billing/portal'

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = 'authenticity_token'
    input.value = csrfToken
    form.appendChild(input)

    document.body.appendChild(form)
    form.submit()
  }

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold">
            <span className="text-primary">プラン</span>・課金管理
          </h1>
          <p className="text-muted-foreground text-lg">プランの確認と<span className="text-primary font-semibold">変更</span>ができます</p>
        </div>

        {/* 現在のプラン */}
        <Card className={`border-border/60 transition-all duration-300 hover:shadow-lg hover:border-border/80 ${isUnlimited ? 'border-primary/50' : ''}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {isUnlimited && <Crown className="w-5 h-5 text-primary" />}
              現在のプラン
              <Badge variant={isPro || isUnlimited ? 'default' : 'secondary'}>{plan}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isUnlimited ? (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  特別プランをご利用中です。
                </p>
                <ul className="mt-2 space-y-1">
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary" />
                    無制限のプライベートリポジトリ
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary" />
                    無制限のストレージ
                  </li>
                </ul>
              </div>
            ) : isPro && subscription ? (
              <div className="space-y-2">
                {subscription.currentPeriodEnd && (
                  <p className="text-sm text-muted-foreground">
                    次回更新日: {format(new Date(subscription.currentPeriodEnd), 'yyyy年M月d日', { locale: ja })}
                  </p>
                )}
                {subscription.cancelAtPeriodEnd && (
                  <p className="text-sm text-destructive">
                    キャンセル済み（期間終了後に Free プランに移行します）
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Free プランをご利用中です
              </p>
            )}
          </CardContent>
          {isPro && !isUnlimited && (
            <CardFooter>
              <Button variant="outline" onClick={handleManage}>
                <CreditCard className="w-4 h-4 mr-2" />
                支払い方法を管理
              </Button>
            </CardFooter>
          )}
        </Card>

        {/* ダウングレード警告（Pro ユーザーで制限超過する場合） */}
        {isPro && !isUnlimited && willExceedLimits && (
          <Alert className="border-warning/50 bg-warning/10">
            <AlertTriangle className="h-5 w-5 text-warning" />
            <AlertTitle className="text-lg font-semibold">ダウングレード時のご注意</AlertTitle>
            <AlertDescription className="mt-2 space-y-3">
              <p className="text-sm">
                現在の使用状況は Free プランの制限を超過しています。ダウングレードすると以下の制限が適用されます。
              </p>

              {willExceedPrivateRepoLimit && (
                <div className="text-sm">
                  <p className="font-medium">
                    プライベートリポジトリ: {privateRepositoryCount}個（制限: {FREE_PRIVATE_REPO_LIMIT}個）
                  </p>
                  <p className="text-warning font-medium mt-1">
                    → すべてのプライベートリポジトリへの push が不可になります
                  </p>
                  <div className="mt-2 p-3 bg-background/50 rounded-md border border-border/50">
                    <p className="text-xs text-muted-foreground mb-2">影響を受けるリポジトリ:</p>
                    <ul className="space-y-1">
                      {privateRepositories.map((name) => (
                        <li key={name} className="text-sm font-mono flex items-center gap-2 text-foreground">
                          <span className="w-2 h-2 rounded-full bg-warning" />
                          {name}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {willExceedStorageLimit && (
                <div className="text-sm">
                  <p className="font-medium">
                    ストレージ: {storageUsedFormatted}（制限: 1 GB）
                  </p>
                  <p className="text-warning font-medium mt-1">
                    → ストレージ超過中は push が不可になります
                  </p>
                </div>
              )}

              <p className="text-sm text-muted-foreground pt-2 border-t border-border/50">
                既存のデータは削除されません。pull とダウンロードは引き続き可能です。
                制限内に収めるには、リポジトリを削除するか公開に変更してください。
              </p>
            </AlertDescription>
          </Alert>
        )}

        {/* プラン比較（Unlimited 以外のみ表示） */}
        {!isUnlimited && (
        <div className="grid gap-6 md:grid-cols-2">
          {/* Free プラン */}
          <Card className={`border-border/60 transition-all duration-300 hover:shadow-lg ${!isPro ? 'border-[1.5px] border-primary shadow-lg shadow-primary/10' : 'hover:border-border/80'}`}>
            <CardHeader>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1 h-8 bg-accent-blue rounded-full"></div>
                <CardTitle className="text-xl">Free</CardTitle>
              </div>
              <CardDescription>個人開発を始めるのに最適</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold mb-4 text-accent-blue">¥0</div>
              <ul className="space-y-2">
                <PlanFeature>パブリックリポジトリ無制限</PlanFeature>
                <PlanFeature>プライベートリポジトリ 1 個</PlanFeature>
                <PlanFeature>1 GB ストレージ</PlanFeature>
              </ul>
            </CardContent>
            <CardFooter>
              {!isPro ? (
                <Button disabled className="w-full">現在のプラン</Button>
              ) : (
                <Button variant="outline" className="w-full" onClick={handleManage}>
                  ダウングレード
                </Button>
              )}
            </CardFooter>
          </Card>

          {/* Pro プラン */}
          <Card className={`relative transition-all duration-300 overflow-hidden ${isPro ? 'border-0 bg-gradient-to-br from-primary/8 via-primary/5 to-primary/8 dark:from-primary/15 dark:via-primary/10 dark:to-primary/15 shadow-2xl shadow-primary/20 dark:shadow-primary/30' : 'border-border/60 hover:shadow-lg hover:border-primary/40'}`}>
            {isPro && (
              <>
                {/* Gradient border effect */}
                <div className="absolute inset-0 rounded-2xl p-[1px] bg-gradient-to-br from-primary/60 via-primary/30 to-primary/60 dark:from-primary/70 dark:via-primary/40 dark:to-primary/70">
                  <div className="h-full w-full rounded-2xl bg-gradient-to-br from-primary/8 via-primary/5 to-primary/8 dark:from-primary/15 dark:via-primary/10 dark:to-primary/15" />
                </div>
                {/* Subtle glow */}
                <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 to-primary/10 dark:from-primary/30 dark:to-primary/20 rounded-2xl blur-xl opacity-50" />
              </>
            )}
            <CardHeader className={isPro ? 'relative z-10' : ''}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-8 bg-gradient-to-b from-primary to-primary/60 rounded-full"></div>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <Sparkles className="w-6 h-6 text-primary" />
                    Pro
                  </CardTitle>
                </div>
                {isPro && <Badge className="bg-primary text-primary-foreground">現在のプラン</Badge>}
              </div>
              <CardDescription>本格的な開発に</CardDescription>
            </CardHeader>
            <CardContent className={isPro ? 'relative z-10' : ''}>
              <div className="text-4xl font-bold text-primary mb-4">
                ¥500
                <span className="text-lg font-normal text-muted-foreground">/月（税別）</span>
              </div>
              <ul className="space-y-2">
                <PlanFeature>パブリックリポジトリ無制限</PlanFeature>
                <PlanFeature highlight>プライベートリポジトリ無制限</PlanFeature>
                <PlanFeature highlight>50 GB ストレージ</PlanFeature>
              </ul>
            </CardContent>
            <CardFooter className={isPro ? 'relative z-10' : ''}>
              {isPro ? (
                <Button disabled className="w-full">現在のプラン</Button>
              ) : (
                <Button className="w-full" onClick={handleUpgrade} disabled={loading}>
                  {loading ? 'リダイレクト中...' : 'アップグレード'}
                </Button>
              )}
            </CardFooter>
          </Card>
        </div>
        )}
      </div>
    </AppLayout>
  )
}

function PlanFeature({ children, highlight = false }: { children: React.ReactNode; highlight?: boolean }) {
  return (
    <li className={`flex items-center gap-2 ${highlight ? 'font-medium' : ''}`}>
      <Check className={`w-4 h-4 ${highlight ? 'text-primary' : 'text-muted-foreground'}`} />
      {children}
    </li>
  )
}
